// Renders announcement media (image/YouTube/Vimeo/direct video URL) inline.
// Shared by RepDashboard.tsx (agent-facing) and AdminDashboard.tsx (admin preview).
//
// Uploads can be any aspect ratio (tall, wide, square, tiny, huge). Images and
// direct video uploads render inside a fixed-height, neutral-background frame
// using object-fit: contain, so the full upload is always visible and never
// cropped, regardless of its native dimensions. YouTube/Vimeo embeds keep
// aspect-video sizing since those iframes already fit their content correctly.

import { useState, type KeyboardEvent } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

function extractYouTubeId(url: string): string | null {
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtu\.be\/)([\w-]{6,})/i);
  return m ? m[1] : null;
}
function extractVimeoId(url: string): string | null {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  return m ? m[1] : null;
}

export function AnnouncementMedia({
  mediaUrl,
  mediaType,
  title,
  className = "mb-3 h-80",
  enableLightbox = false,
}: {
  mediaUrl?: string | null;
  mediaType?: string | null;
  title: string;
  className?: string;
  // Opt-in only (default false) so existing callers, notably AdminDashboard's
  // announcement preview thumbnail, keep their current non-interactive
  // behavior unchanged. RepDashboard's Associate Dashboard rendering is the
  // only caller that turns this on, per the client's "images too small to
  // read" request.
  enableLightbox?: boolean;
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (!mediaUrl || !mediaType || mediaType === "none") return null;

  if (mediaType === "image") {
    const openLightbox = () => setLightboxOpen(true);
    return (
      <>
        <div className={`w-full flex items-center justify-center bg-gray-50 rounded-xl border border-gray-100 overflow-hidden ${className}`}>
          <img
            src={mediaUrl}
            alt={title}
            loading="lazy"
            className={`w-full h-full object-contain ${enableLightbox ? "cursor-zoom-in" : ""}`}
            {...(enableLightbox
              ? {
                  role: "button" as const,
                  tabIndex: 0,
                  onClick: openLightbox,
                  onKeyDown: (e: KeyboardEvent<HTMLImageElement>) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openLightbox();
                    }
                  },
                  "aria-label": `Enlarge image: ${title}`,
                }
              : {})}
          />
        </div>
        {enableLightbox && (
          <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
            <DialogContent
              showCloseButton
              className="max-w-4xl w-[95vw] sm:w-[90vw] p-3 sm:p-4 bg-black/95 border-none [&_svg]:text-white/80 [&_svg]:hover:text-white"
            >
              <DialogTitle className="text-sm font-medium text-white/80 text-center sm:text-left">{title}</DialogTitle>
              <img src={mediaUrl} alt={title} className="w-full max-h-[80vh] object-contain rounded-lg" />
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  }

  if (mediaType === "youtube") {
    const videoId = extractYouTubeId(mediaUrl);
    if (!videoId) return null;
    return (
      <div className={`w-full aspect-video rounded-xl overflow-hidden border border-gray-100 ${className}`}>
        <iframe src={`https://www.youtube.com/embed/${videoId}`} title={title}
          className="w-full h-full" frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen />
      </div>
    );
  }

  if (mediaType === "vimeo") {
    const videoId = extractVimeoId(mediaUrl);
    if (!videoId) return null;
    return (
      <div className={`w-full aspect-video rounded-xl overflow-hidden border border-gray-100 ${className}`}>
        <iframe src={`https://player.vimeo.com/video/${videoId}`} title={title}
          className="w-full h-full" frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
      </div>
    );
  }

  // video_url — direct link (e.g. uploaded to Wibiz media storage)
  return (
    <div className={`w-full flex items-center justify-center bg-black rounded-xl border border-gray-100 overflow-hidden ${className}`}>
      <video src={mediaUrl} controls preload="metadata" className="w-full h-full object-contain" />
    </div>
  );
}
